import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action } from '@ember/object';
import StockTakingModel from 'bcl/models/stocktaking';
import { on } from '@ember/modifier';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import SelectStockTaking from 'bcl/components/input/select-stocktaking';
import Store from '@ember-data/store';
import euro from 'bcl/helpers/euro';
import { fn } from '@ember/helper';
import { not } from 'ember-truth-helpers';
import { findRecord } from '@warp-drive/utilities/json-api';
import type NotificationService from 'bcl/services/notification';
import * as Sentry from '@sentry/ember';

interface ReportsSignature {
  // The arguments accepted by the component
  Args: {};
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class ReportsComponent extends Component<ReportsSignature> {
  @service declare store: Store;
  @service declare notification: NotificationService;
  @tracked startStockTaking: StockTakingModel | null = null;
  @action changeStartStockTaking(stocktaking: StockTakingModel) {
    this.startStockTaking = stocktaking;
  }
  @tracked endStockTaking: StocktakingModel | null = null;
  @action changeEndStockTaking(stocktaking: StockTakingModel) {
    this.endStockTaking = stocktaking;
  }

  @tracked selectedReport: string = 'umsatz';
  @action changeSelectedReport(event: Event) {
    const value = (event.target as HTMLSelectElement).value;

    this.selectedReport =
      this.availableReports.find((report) => report.key === value)?.key ||
      'inventur';
    console.log(this.selectedReport);
  }
  availableReports: { name: string; key: string }[] = [
    { name: 'Inventur', key: 'inventur' },
    { name: 'Umsatz', key: 'umsatz' },
    // { name: 'Umsatz einzelner Artikel', key: 'umsatz_einzelner_artikel' },
  ];
  @action async getReport() {
    if (this.selectedReport == 'umsatz') {
      if (this.startStockTaking?.id == this.endStockTaking?.id) {
        this.notification.warning('Die Start- und End-Inventur sind gleich.');
        return;
      }
      if (this.startStockTaking.created_at > this.endStockTaking.created_at) {
        this.notification.warning(
          'Die Startinventur muss vor der End-Inventur liegen.'
        );
        return;
      }
    }
    const resp = await fetch(`/api/report/${this.selectedReport}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sidStart: this.startStockTaking?.id,
        sidEnd: this.endStockTaking?.id,
      }),
    });
    this.reportData = await resp.json();
    if (this.selectedReport === 'umsatz') {
      return this.processUmsatzReport();
    } else {
      return this.processInventurReport();
    }
  }
  @action processInventurReport() {
    Sentry.captureException(new Error('wollte inventur report haben'));
    alert('Fehler bei der Berechnung der Inventur');
    // funktioniert nach dem Umbau leider nicht
    return;
    const perLocation = {};
    for (const entry of this.reportData) {
      const amount = entry.actual_amount;

      const loc_item = this.store.peekRecord(
        'location-item',
        entry.locationItem_id
      );
      if (!perLocation[loc_item?.location.id]) {
        perLocation[loc_item?.location.id] = {
          total_deposit: 0,
          total_value: 0,
          entries: [],
          name: loc_item?.location.name,
        };
      }

      const item = this.store.peekRecord('item', loc_item?.item.id);
      const amount_container = amount / item.container_size;
      const amount_container_ceil = Math.ceil(amount_container);
      const amount_container_floor = Math.floor(amount_container);

      // find normal supplier (standard_order und nicht special)
      // const supplier = this.store.peekRecord('supplier', item?.);
      const suplItems = item.supplierItems.filter(
        (si) => si.standard_order && !si.special
      );
      let suplItem = suplItems[0];
      if (!suplItem) {
        suplItem = item.supplierItems[0];
      }
      if (!suplItem) {
        console.warn(`No suitable supplier found: ${item.id} ${item.name}`);
      }
      const price = suplItem?.price || 0;

      const row = {
        item: item,
        price,
        amount,
        amount_container,
        amount_container_ceil,
        amount_container_floor,
        value_total: amount_container * price,
        deposit_container: item.deposit_container,
        deposit_single: item.deposit_single,
        deposit_total:
          amount_container_ceil * item.deposit_container +
          amount * item.deposit_single,
      };
      perLocation[loc_item?.location.id].entries.push(row);
      perLocation[loc_item?.location.id].total_deposit += row.deposit_total;
      perLocation[loc_item?.location.id].total_value += row.value_total;
    }
    for (const locId in perLocation) {
      perLocation[locId].entries.sort((a, b) =>
        a.item.name.localeCompare(b.item.name)
      );
    }
    this.reportData = perLocation;
  }
  @action
  async processUmsatzReport() {
    const startData = this.reportData?.start;
    const endData = this.reportData?.end;
    const ordersIds = this.reportData?.orders;
    // Ensure all orders are loaded
    const ordersPromises = await Promise.all(
      ordersIds.map((id) => this.store.request(findRecord('order', `${id}`)))
    );
    const orders = ordersPromises.map((prom) => prom.content.data);
    console.log(ordersIds, orders);
    await sleep(40);
    // let orderEntries = this.store.peekAll('order-entry').filter((oe) => {
    //   return ordersIds.includes(+oe.order.id);
    // });
    // if (orderEntries.length === 0) {
    //   alert(
    //     'Es wurden keine Bestellungen gefunden! Bitte erneut versuchen. Sollte das Problem weiterhin bestehen, bitte den Support kontaktieren.'
    //   );
    // }
    const perItem = {};

    for (let i = 0; i < startData.length; i++) {
      const startEle = this.store.peekRecord(
        'location-item',
        startData[i].locationItem_id
      );
      // const endEle = this.store.peekRecord(
      //   'location-item',
      //   endData[i].locationItem_id,
      // );

      const itemID = startEle.item.id;

      if (!perItem[itemID]) {
        perItem[itemID] = {
          item: startEle.item,
          start: 0,
          ordered: 0,
          end: 0,
          difference: 0,
          price: startEle.item.supplierItems[0]?.price || 0,
          unitsPerContainer:
            startEle.item.single_size / startEle.item.serving_size,
          revenue: 0,
        };
      }
      perItem[itemID].start = +startData[i].actual_amount;
      perItem[itemID].end = +endData[i].actual_amount;
      perItem[itemID].ordered = 0;
      for (const order of orders) {
        for (const entry of order.orderEntries) {
          if (entry.supplier_item.item.id === itemID) {
            perItem[itemID].ordered += entry.amount;
          }
        }
      }
    }

    // Calculate difference, served, and revenue for each item
    for (const itemID in perItem) {
      const itemData = perItem[itemID];
      itemData.difference = itemData.start - itemData.end + itemData.ordered;
      itemData.served =
        Math.round(itemData.unitsPerContainer * itemData.difference * 100) /
        100;
      itemData.revenue = itemData.served * (itemData.item.gross_price || 0);
    }
    const perItemArray = Object.values(perItem).sort((a, b) =>
      a.item.name.localeCompare(b.item.name)
    );

    this.reportData = {
      entries: perItemArray,
      total: perItemArray.reduce((sum, entry) => sum + entry.revenue, 0),
    };
  }
  @tracked reportData: string | null = null;
  @action download(type: 'all' | number) {
    console.log('download report', type);
    if (type === 'all') {
      if (this.selectedReport === 'umsatz') {
        return this.downloadUmsatz();
      } else {
        return this.downloadInventurAll();
      }
    } else {
      return this.downloadInventurSingle(type);
    }
  }
  downloadInventurSingle(num: number) {
    const perItem = {};
    const locData = this.reportData[num];
    for (const entry of locData.entries) {
      const item_id = entry.item.id;
      if (!perItem[item_id]) {
        perItem[item_id] = {
          item: entry.item,
          price: entry.price,
          amount: 0,
          amount_container: 0,
          deposit_total: 0,
          value_total: 0,
          entries: [],
        };
      }
      perItem[item_id].entries.push(entry);
      perItem[item_id].amount += entry.amount;
      perItem[item_id].amount_container += entry.amount_container;
      perItem[item_id].deposit_total += entry.deposit_total;
      perItem[item_id].value_total += entry.value_total;
    }
    this.createInventurCSV(perItem, locData.name);
  }

  downloadUmsatz() {
    const itemsArray = this.reportData.entries;
    console.log(itemsArray);
    // You can now use itemsArray for further processing or exporting
    const csvRows = [];
    // Header
    csvRows.push(
      [
        'Artikel',
        'Start',
        'Lieferungen',
        'Ende',
        'Differenz',
        'Ausschank',
        'Umsatz',
      ].join(';')
    );
    console.log(itemsArray[0]);

    for (const entry of itemsArray) {
      csvRows.push(
        [
          `"${entry.item.name} (${entry.item.gebinde})"`,
          entry.start,
          entry.ordered,
          entry.end,
          entry.difference,
          entry.served,
          centToEuro(entry.revenue),
        ].join(';')
      );
    }
    csvRows.push(
      [`INSGESAMT`, '', '', '', '', '', centToEuro(this.reportData.total)].join(
        ';'
      )
    );

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', this.selectedReport + '-alle.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  downloadInventurAll() {
    const perItem = {};
    for (const loc_id in this.reportData) {
      const locData = this.reportData[loc_id];
      for (const entry of locData.entries) {
        const item_id = entry.item.id;
        if (!perItem[item_id]) {
          perItem[item_id] = {
            item: entry.item,
            price: entry.price,
            amount: 0,
            amount_container: 0,
            deposit_total: 0,
            value_total: 0,
            entries: [],
          };
        }
        perItem[item_id].entries.push(entry);
        perItem[item_id].amount += entry.amount;
        perItem[item_id].amount_container += entry.amount_container;
        perItem[item_id].deposit_total += entry.deposit_total;
        perItem[item_id].value_total += entry.value_total;
      }
    }
    this.createInventurCSV(perItem, 'alle');
  }
  createInventurCSV(perItem, filename) {
    console.log(perItem);
    const itemsArray = Object.values(perItem).sort((a, b) =>
      a.item.name.localeCompare(b.item.name)
    );
    console.log(itemsArray);
    // You can now use itemsArray for further processing or exporting
    const csvRows = [];
    // Header
    csvRows.push(
      [
        'Artikel',
        'Preis Gebinde',
        'Pfand Einzel',
        'Pfand Gebinde',
        'Bestand (Stück)',
        'Warenwert',
        'Pfand gesamt',
      ].join(';')
    );

    for (const entry of itemsArray) {
      csvRows.push(
        [
          `"${entry.item.name} (${entry.item.gebinde})"`,
          centToEuro(entry.price),
          centToEuro(entry.item.deposit_single),
          centToEuro(entry.item.deposit_container),
          `${Math.floor(entry.amount_container)} (${entry.amount})`,
          centToEuro(entry.value_total),
          centToEuro(entry.deposit_total),
        ].join(';')
      );
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const utcDate = new Date(this.startStockTaking.created_at)
      .toISOString()
      .split('T')[0];
    link.setAttribute(
      'download',
      `${this.selectedReport}-${utcDate}-${filename}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  <template>
    <div>
      <p>Hier können Sie Berichte erstellen. Wähle einen Berichtstyp und die
        Inventur(en) und klicke auf "Bericht erstellen".</p>
    </div>
    <div
      class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2 col-span-6"
    >
      <label
        for="report-select"
        class="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:text-right"
      >Berichtstyp:</label>
      <select
        id="report-select"
        value={{this.selectedReport}}
        class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
        {{on "change" this.changeSelectedReport}}
      >
        {{#each this.availableReports as |report|}}
          <option
            value={{report.key}}
            selected={{eq report.key this.selectedReport}}
          >
            {{report.name}}
          </option>
        {{/each}}
      </select>
    </div>
    <div class="mt-4">
      <SelectStockTaking
        @name="start-stocktaking"
        @label={{if
          (eq this.selectedReport "inventur")
          "Inventur:"
          "Start-Inventur:"
        }}
        @value={{this.startStockTaking}}
        @onChange={{this.changeStartStockTaking}}
      />
    </div>
    {{#if
      (or
        (eq this.selectedReport "umsatz")
        (eq this.selectedReport "umsatz_einzelner_artikel")
      )
    }}
      <div class="mt-4">
        <SelectStockTaking
          @name="end-stocktaking"
          @label="End-Inventur"
          @value={{this.endStockTaking}}
          @onChange={{this.changeEndStockTaking}}
        />
      </div>
    {{/if}}
    <div
      class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2 col-span-6"
    >
      <div class="sm:col-start-2">

        <button
          {{on "click" this.getReport}}
          type="button"
          class="mt-4 inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Bericht erstellen
        </button>
        <button
          disabled={{not this.reportData}}
          {{on "click" (fn this.download "all")}}
          type="button"
          class="mt-4 inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 disabled:bg-slate-600 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Bericht für alle herunterladen
        </button>

      </div>
    </div>
    <div>
      {{#if (eq this.selectedReport "umsatz")}}
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Artikel
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Start
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Lieferungen
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Ende
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Differenz
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Ausschank
                </th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Umsatz
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              {{#each this.reportData.entries as |entry|}}
                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.item.name}}
                    ({{entry.item.gebinde}})
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.start}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.ordered}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.end}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.difference}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{entry.served}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{euro entry.revenue}}
                  </td>
                </tr>
              {{/each}}
              <tr>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  INSGESAMT
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {{euro this.reportData.total}}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      {{else}}
        {{#each-in this.reportData as |loc_id perLoc|}}
          <h2 class="text-lg font-medium leading-6 text-gray-900 mt-6 mb-2">
            {{perLoc.name}}
            <button
              {{on "click" (fn this.download loc_id)}}
              type="button"
              class="ml-5 mt-4 inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 disabled:bg-slate-600 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Bericht für
              {{perLoc.name}}
              herunterladen
            </button>
          </h2>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Artikel
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Preis Gebinde
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Pfand einzel
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Pfand Gebinde
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Bestand (Stück)
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Warenwert
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Pfand gesamt
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                {{#each perLoc.entries as |entry|}}
                  <tr>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{entry.item.name}}
                      ({{entry.item.gebinde}}
                      )
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{euro entry.price}}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{euro entry.deposit_single}}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{euro entry.deposit_container}}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{entry.amount_container_floor}}
                      ({{entry.amount}})
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{euro entry.value_total}}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{euro entry.deposit_total}}
                    </td>
                  </tr>
                {{/each}}
                <tr>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    INSGESAMT
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">

                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{euro perLoc.total_value}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{euro perLoc.total_deposit}}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        {{/each-in}}
      {{/if}}
    </div>
  </template>
}

function centToEuro(cents) {
  return (cents / 100).toFixed(2);
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
